/**
 * EncounterEmptyState
 *
 * Displays when no encounters match the current filters,
 * or when there are no encounters at all.
 * Enterprise design with polished visuals and clear CTAs.
 */

import React from 'react';
import { CalendarIcon, MagnifyingGlassIcon, PlusIcon, FunnelIcon } from '@heroicons/react/24/outline';
import { useEncounterList } from '../EncounterListContext';

export function EncounterEmptyState() {
  const { config, filters, activeFilters, clearFilters } = useEncounterList();

  const hasFilters = activeFilters.length > 0;
  const hasSearch = !!filters.search;

  let icon: React.ReactNode;
  // Standalone practices book appointments; "encounter" is the connected (EHR) word.
  const noun = config.mode === 'standalone' ? 'appointments' : 'encounters';
  let title = `No ${noun} found`;
  let description = '';
  let action: React.ReactNode = null;

  if (hasSearch) {
    icon = (
      <div className="mx-auto w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mb-4">
        <MagnifyingGlassIcon className="h-8 w-8 text-gray-400" />
      </div>
    );
    title = 'No results found';
    description = `No ${noun} match "${filters.search}". Try a different search term.`;
    action = (
      <button
        type="button"
        onClick={clearFilters}
        className="mt-5 px-5 py-2.5 text-sm font-medium text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-xl transition-colors"
      >
        Clear Search
      </button>
    );
  } else if (hasFilters) {
    icon = (
      <div className="mx-auto w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mb-4">
        <FunnelIcon className="h-8 w-8 text-gray-400" />
      </div>
    );
    title = `No matching ${noun}`;
    description = `No ${noun} match your current filters. Try adjusting or clearing them.`;
    action = (
      <button
        type="button"
        onClick={clearFilters}
        className="mt-5 px-5 py-2.5 text-sm font-medium text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-xl transition-colors"
      >
        Clear All Filters
      </button>
    );
  } else if (config.mode === 'connected') {
    icon = (
      <div className="mx-auto w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center mb-4">
        <CalendarIcon className="h-8 w-8 text-blue-400" />
      </div>
    );
    title = 'No encounters yet';
    description = 'No encounters have been synced from your EHR system yet. They will appear here once available.';
  } else {
    icon = (
      <div className="mx-auto w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center mb-4">
        <CalendarIcon className="h-8 w-8 text-blue-400" />
      </div>
    );
    title = 'No appointments scheduled';
    description = 'Get started by creating a new appointment.';
    if (config.canCreate && config.onCreateClick) {
      action = (
        <button
          type="button"
          onClick={config.onCreateClick}
          className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-xl shadow-sm transition-all hover:shadow-md"
        >
          <PlusIcon className="h-5 w-5" />
          New Appointment
        </button>
      );
    }
  }

  return (
    <div className="text-center py-16 px-4">
      {icon}
      <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
      {description && (
        <p className="mt-2 text-sm text-gray-500 max-w-md mx-auto leading-relaxed">{description}</p>
      )}
      {action}
    </div>
  );
}

export default EncounterEmptyState;
